import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Button, Divider, Steps, Tooltip } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import { CardMainLayout, Catcher, FileTypeIcons, cardLayoutOptions } from '../';
import dayjs from 'dayjs';
import { apiConfig } from '../../REST';
import { IReportsListAPI } from '../../interfaces';
import { useMonthlyUsageList, useMonthlyUsageTotals, useProfile } from '../../hooks';
import { getInvoicesMonthlyTotal, getMonthlyUsageList, setMonthlyUsageList } from '../../middleware';
import { MonthlyUsageItem } from './Components';
import Styles from '../styles/m_itemCardStyles.less';

interface IRecordsByDate {
    [key: string]: any;
}

export const MonthlyUsageList: React.FC = observer(() => {
    const { companyId: myCompanyId, currency } = useProfile();
    const monthlyUsageList = useMonthlyUsageList();
    const totals = useMonthlyUsageTotals();

    const createFlow = (): any => {
        const flow: any = [];

        const groupedByDate = monthlyUsageList.reduce((result: IRecordsByDate, record) => {
            const date = dayjs(record.startDate);
            const year = date.format('YYYY');
            const month = date.format('MMMM');

            if (!result[year]) {
                result[year] = {};
            }

            if (!result[year][month]) {
                result[year][month] = [];
            }

            result[year][month].push(record);

            return result;
        }, {});

        const groupedByDateArr = Object.entries(groupedByDate).sort((a, b) => {
            return parseInt(b[0]) - parseInt(a[0]);
        });

        if (!groupedByDateArr.length) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%', fontSize: 30 }}>
                    <h1>No monthly usage yet.</h1>
                </div>
            );
        }

        groupedByDateArr.forEach((year, index) => {
            flow.push(<h1 style={{ fontSize: 16, margin: 10 }}>{year[0]}</h1>);

            const months = Object.entries(year[1]);

            months.sort((a: any, b: any) => {
                const dateA = dayjs(a[1][0].startDate);
                const dateB = dayjs(b[1][0].startDate);
                return dateB.valueOf() - dateA.valueOf();
            });

            const items = months.map((month: any) => {
                const files = {
                    pdf: month[1]?.find((item: any) => item.fileType === 'pdf') || {},
                    csv: month[1]?.find((item: any) => item.fileType === 'csv') || {},
                };

                const date = dayjs(month[1][0].startDate);

                const earliestDate = dayjs(month[1][0].startDate).format('YYYY-MM-DD');
                const latestDate = dayjs(month[1][0].endDate).format('YYYY-MM-DD');

                const startOfMonth = date.startOf('month').format('YYYY-MM-DD');
                const endOfMonth = date.endOf('month').format('YYYY-MM-DD');

                const fountTotal = totals[startOfMonth];

                return {
                    title: (
                        <div>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: 10,
                                    marginBottom: 15,
                                    justifyContent: 'space-between',
                                }}
                            >
                                <span style={{ fontSize: 18 }}>
                                    {month[0]}{' '}
                                    <span style={{ fontSize: 14 }}>
                                        (Charging period: {earliestDate} - {latestDate})
                                    </span>
                                </span>
                                {fountTotal ? (
                                    <span>
                                        Total: {fountTotal}
                                        {currency}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    ),
                    description: (
                        <div>
                            <>
                                {fountTotal ? (
                                    <div style={{ marginBottom: 20 }}>
                                        <MonthlyUsageItem
                                            myCompanyId={myCompanyId || ''}
                                            startDate={startOfMonth}
                                            endDate={endOfMonth}
                                        />
                                    </div>
                                ) : null}
                            </>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: 10,
                                    marginBottom: 15,
                                    justifyContent: 'space-between',
                                }}
                            >
                                <span style={{ fontSize: 16 }}>Monthly Reports:</span>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    {files.pdf?.file ? (
                                        <Tooltip title={'Download Total Usage in PDF'} placement="top">
                                            <a href={`${apiConfig.apiUrl()}${files.pdf.file}`}>
                                                <Button icon={<FilePdfOutlined />}>PDF</Button>
                                            </a>
                                        </Tooltip>
                                    ) : null}
                                    {files.csv?.file ? (
                                        <Tooltip title={'Download Total Usage in CSV'} placement="top">
                                            <a href={`${apiConfig.apiUrl()}${files.csv.file}`}>
                                                <Button icon={<FileTypeIcons fileType={'csv'} />}>CSV</Button>
                                            </a>
                                        </Tooltip>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ),
                };
            });

            flow.push(<Steps progressDot current={items.length} direction="vertical" items={items} />);

            if (index !== groupedByDateArr.length - 1) {
                flow.push(<Divider />);
            }
        });

        return flow;
    };

    const _getMonthlyUsageList = async () => {
        const fromDate = dayjs().subtract(6, 'month');
        const toDate = dayjs();

        const resp = await getMonthlyUsageList({
            companyId: myCompanyId,
            usageScheduled: true,
            fromMonth: fromDate.toString(),
            tillMonth: toDate.toString(),
        });
        setMonthlyUsageList(resp as IReportsListAPI);
    };

    useEffect(() => {
        _getMonthlyUsageList();
        getInvoicesMonthlyTotal();
    }, []);

    return (
        <Catcher>
            <article className={Styles.main}>
                <CardMainLayout
                    columnOption={cardLayoutOptions.monthlyUsage}
                    title={'Monthly Usage'}
                    idSpinners={['GET_MONTHLY_USAGE_REPORTS']}
                >
                    <p>
                        Here, you can access detailed summaries of your monthly usage, providing a clear overview of
                        your activity. Additionally, this page allows you to download invoice reports for each month.
                    </p>
                    <p style={{ fontWeight: 'bold' }}>
                        Please note: Monthly usage data is retained for six months, so we recommend downloading any
                        reports you may need for future reference.
                    </p>
                    <Divider />
                    {createFlow()}
                </CardMainLayout>
            </article>
        </Catcher>
    );
});
